import React, { useEffect, useMemo, useState } from 'react'
import {
    Card,
    CardContent,
    Button,
    CircularProgress,
    TextField,
    MenuItem,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import CloseIcon from '@material-ui/icons/Close'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import { Alert } from '@material-ui/lab'
import Moment from 'react-moment'
import ReactJson from 'react-json-view'
import {
    adminSearchInteractionResponses,
    adminGetInteractionResponse,
    adminGetInteractionFilters,
} from 'actions/applicants'
import { getClientsList } from 'actions/client'
import { Toast } from 'components/shared/Toaster'

const PAGE_SIZES = [10, 20, 50, 100]

const codeChipStyle = (code) => {
    switch (code) {
        case 'VALID': return { background: 'var(--badge-approved-bg)', color: 'var(--status-approved)' }
        case 'ERROR': return { background: 'var(--badge-declined-bg)', color: 'var(--status-declined)' }
        case 'NO_RESPONSE':
        case 'NO_CONTENT': return { background: 'var(--badge-watch-bg)', color: '#e65100' }
        default: return { background: 'var(--bg-canvas)', color: '#555' }
    }
}

// Column defs — { header, get(row) }. hasAny() filters out columns that are empty across all rows.
const COLUMN_DEFS = [
    { key: 'createdAt', header: 'Created', get: (r) => r.createdAt },
    { key: 'type', header: 'Type', get: (r) => r.type },
    { key: 'name', header: 'Name', get: (r) => r.name || r.interactionName },
    { key: 'code', header: 'Code', get: (r) => r.code },
    { key: 'client', header: 'Client', get: (r) => r.clientName || r.client?.name || r.clientId || r.client?.id },
    { key: 'application', header: 'Application', get: (r) => r.applicationUuid, mono: true },
    { key: 'uuid', header: 'UUID', get: (r) => r.uuid || r.id, mono: true },
]

const isEmpty = (v) => v == null || v === '' || v === '—'

const InteractionResponses = () => {
    const [clients, setClients] = useState([])
    const [clientsLoading, setClientsLoading] = useState(false)
    const [params, setParams] = useState({
        type: '',
        name: '',
        clientId: '',
        uuid: '',
        clientReference: '',
        code: '',
        createdFrom: '',
        createdTo: '',
    })
    const [filterOptions, setFilterOptions] = useState({ types: [], names: [], codes: [] })
    const [pageLimit, setPageLimit] = useState(20)
    const [pageNumber, setPageNumber] = useState(0)
    const [results, setResults] = useState([])
    const [loading, setLoading] = useState(false)
    const [hasSearched, setHasSearched] = useState(false)
    const [detail, setDetail] = useState(null)
    const [detailLoading, setDetailLoading] = useState(false)

    useEffect(() => {
        setClientsLoading(true)
        getClientsList({}, {}, true)(() => {})
            .then((res) => {
                const list = res?.response || res?.responseBody || res || []
                setClients(Array.isArray(list) ? list : [])
            })
            .catch(() => Toast({ type: 'error', message: 'Failed to load clients' }))
            .finally(() => setClientsLoading(false))
        adminGetInteractionFilters()
            .then((res) => setFilterOptions({
                types: res?.types || [],
                names: res?.names || [],
                codes: res?.codes || [],
            }))
            .catch(() => Toast({ type: 'error', message: 'Failed to load filter options' }))
    }, [])

    const setParam = (key) => (e) => {
        const { value } = e.target
        setParams((p) => ({ ...p, [key]: value }))
    }

    const handleTypeChange = (e) => {
        const type = e.target.value
        setParams((p) => ({ ...p, type, name: '' }))
    }

    const runSearch = (page = 0) => {
        setLoading(true)
        setHasSearched(true)
        setPageNumber(page)
        adminSearchInteractionResponses({ params, pageLimit, pageNumber: page })()
            .then((res) => {
                const list = res?.response || res?.responseBody || []
                setResults(Array.isArray(list) ? list : [])
            })
            .catch(() => Toast({ type: 'error', message: 'Failed to load interaction responses' }))
            .finally(() => setLoading(false))
    }

    const openDetail = (id) => {
        if (!id) return
        setDetail({ id, data: null })
        setDetailLoading(true)
        adminGetInteractionResponse({ interactionResponseId: id })()
            .then((res) => setDetail({ id, data: res }))
            .catch(() => {
                Toast({ type: 'error', message: 'Failed to load interaction response' })
                setDetail(null)
            })
            .finally(() => setDetailLoading(false))
    }

    // Only render columns that have at least one non-empty value in the current result set.
    const visibleColumns = useMemo(() => {
        if (!results.length) return COLUMN_DEFS
        return COLUMN_DEFS.filter((c) => results.some((r) => !isEmpty(c.get(r))))
    }, [results])

    const copyJson = () => {
        if (!detail?.data) return
        navigator.clipboard.writeText(JSON.stringify(detail.data, null, 2))
            .then(() => Toast({ type: 'success', message: 'Copied to clipboard' }))
            .catch(() => Toast({ type: 'error', message: 'Copy failed' }))
    }

    const cellStyle = { padding: '8px 10px', borderBottom: '1px solid #eee', fontSize: 13, verticalAlign: 'top' }
    const headStyle = { ...cellStyle, background: 'var(--bg-canvas)', fontWeight: 600, textAlign: 'left', borderBottom: '2px solid #ddd' }

    return (
        <>
            <div className='d-flex justify-content-between align-items-center mb-3'>
                <h4 className='mb-0'>Interaction Responses</h4>
            </div>

            <Card variant='outlined' className='mb-3'>
                <CardContent>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                        <TextField
                            select
                            label='Client'
                            value={params.clientId}
                            onChange={setParam('clientId')}
                            size='small'
                            variant='outlined'
                            disabled={clientsLoading}
                            helperText={clientsLoading ? 'Loading clients…' : `${clients.length} clients`}
                        >
                            <MenuItem value=''><em>Any</em></MenuItem>
                            {clients
                                .slice()
                                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                .map((c) => (
                                    <MenuItem key={c.id || c.uuid} value={String(c.id ?? c.uuid)}>
                                        {c.name || '—'}
                                    </MenuItem>
                                ))}
                        </TextField>
                        <TextField select label='Type' value={params.type} onChange={handleTypeChange} size='small' variant='outlined'>
                            <MenuItem value=''><em>Any</em></MenuItem>
                            {filterOptions.types.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                        </TextField>
                        <TextField select label='Name' value={params.name} onChange={setParam('name')} size='small' variant='outlined'>
                            <MenuItem value=''><em>Any</em></MenuItem>
                            {filterOptions.names.map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                        </TextField>
                        <TextField select label='Code' value={params.code} onChange={setParam('code')} size='small' variant='outlined'>
                            <MenuItem value=''><em>Any</em></MenuItem>
                            {filterOptions.codes.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </TextField>
                        <TextField label='Client Reference' value={params.clientReference} onChange={setParam('clientReference')} size='small' variant='outlined' />
                        <TextField label='Interaction UUID' value={params.uuid} onChange={setParam('uuid')} size='small' variant='outlined' />
                        <TextField
                            label='From'
                            type='date'
                            value={params.createdFrom}
                            onChange={setParam('createdFrom')}
                            size='small'
                            variant='outlined'
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ max: params.createdTo || undefined }}
                        />
                        <TextField
                            label='To'
                            type='date'
                            value={params.createdTo}
                            onChange={setParam('createdTo')}
                            size='small'
                            variant='outlined'
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: params.createdFrom || undefined }}
                        />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
                        <TextField
                            select
                            label='Page size'
                            value={pageLimit}
                            onChange={(e) => setPageLimit(Number(e.target.value))}
                            size='small'
                            variant='outlined'
                            style={{ width: 120 }}
                        >
                            {PAGE_SIZES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </TextField>
                        <Button
                            startIcon={<SearchIcon />}
                            variant='contained'
                            color='primary'
                            onClick={() => runSearch(0)}
                            disabled={loading}
                        >
                            Search
                        </Button>
                        {loading && <CircularProgress size={20} />}
                    </div>
                </CardContent>
            </Card>

            {hasSearched && results.length === 0 && !loading && (
                <Alert severity='info'>No results. Adjust the filters and try again.</Alert>
            )}

            {results.length > 0 && (
                <Card variant='outlined'>
                    <CardContent>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr>
                                        {visibleColumns.map((c) => <th key={c.key} style={headStyle}>{c.header}</th>)}
                                        <th style={headStyle} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {results.map((row) => {
                                        const id = row.uuid || row.id
                                        return (
                                            <tr key={id}>
                                                {visibleColumns.map((c) => {
                                                    const value = c.get(row)
                                                    let rendered
                                                    if (isEmpty(value)) rendered = <span style={{ color: '#bbb' }}>—</span>
                                                    else if (c.key === 'createdAt') rendered = <span style={{ whiteSpace: 'nowrap' }}><Moment format='YYYY-MM-DD HH:mm:ss'>{value}</Moment></span>
                                                    else if (c.key === 'code') rendered = <Chip label={value} size='small' style={codeChipStyle(value)} />
                                                    else rendered = value
                                                    return (
                                                        <td key={c.key} style={c.mono ? { ...cellStyle, fontFamily: 'monospace', fontSize: 11 } : cellStyle}>
                                                            {rendered}
                                                        </td>
                                                    )
                                                })}
                                                <td style={{ ...cellStyle, whiteSpace: 'nowrap', width: 1 }}>
                                                    <Button size='small' onClick={() => openDetail(id)}>View</Button>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                            <span style={{ fontSize: 13, color: '#666' }}>
                                Page {pageNumber + 1} — showing {results.length}
                            </span>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <Button size='small' variant='outlined' disabled={loading || pageNumber === 0} onClick={() => runSearch(Math.max(0, pageNumber - 1))}>
                                    Previous
                                </Button>
                                <Button size='small' variant='outlined' disabled={loading || results.length < pageLimit} onClick={() => runSearch(pageNumber + 1)}>
                                    Next
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth='lg' fullWidth>
                <DialogTitle>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Interaction response</span>
                        <div>
                            <IconButton size='small' onClick={copyJson} title='Copy JSON'><FileCopyIcon fontSize='small' /></IconButton>
                            <IconButton size='small' onClick={() => setDetail(null)}><CloseIcon /></IconButton>
                        </div>
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#666' }}>{detail?.id}</div>
                </DialogTitle>
                <DialogContent dividers>
                    {detailLoading && <div style={{ padding: 40, textAlign: 'center' }}><CircularProgress /></div>}
                    {!detailLoading && detail?.data && (
                        <ReactJson
                            src={detail.data}
                            name={false}
                            collapsed={2}
                            displayDataTypes={false}
                            displayObjectSize={false}
                            enableClipboard={(copy) => {
                                navigator.clipboard.writeText(typeof copy.src === 'string' ? copy.src : JSON.stringify(copy.src, null, 2))
                            }}
                            style={{ fontSize: 12 }}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </>
    )
}

export default InteractionResponses
